import * as React from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';

/**
 * URL `search` scalar sidecar param (CU-86cbek5j5, extracted to a neutral
 * home).
 * ==========================================================================
 * Neutral home so both the catalog (`features/media-units/hooks/
 * use-catalog-sidecar-params.ts`, which re-exports these) and the map search
 * box (`features/map-search/hooks/use-map-unified-search.ts`) read/write the
 * same URL `search` term without a cross-feature import — cross-feature
 * import of `features/media-units` from `features/map-search` is forbidden
 * (WEB_ARCHITECTURE.md:36).
 */

// Cast helper: navigate() without `from` has no concrete search type. The setter
// below uses the FUNCTIONAL search updater (search reads LIVE state at commit time
// instead of a captured snapshot) so a setter that fires close to another
// filter-state setter never clobbers what the other just wrote (CU-86cataqt9 fix).
type AnyNavigate = (opts: {
  search: (prev: Record<string, unknown>) => Record<string, unknown>;
  replace: boolean;
}) => void;

/** Reads the `search` scalar from the URL search object. */
export function readSearchParam(search: Record<string, unknown>): string {
  const raw = search['search'];
  return typeof raw === 'string' ? raw : '';
}

export function buildNextSearchParam(
  current: Record<string, unknown>,
  value: string,
): Record<string, unknown> {
  const next = { ...current };
  const trimmed = value.trim();
  if (trimmed) next['search'] = value;
  else delete next['search'];
  return next;
}

/** Reads the `search` scalar from the URL and returns a setter that writes it back. */
export function useSearchParam(): [string, (value: string) => void] {
  const search = useSearch({ strict: false, structuralSharing: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const value = React.useMemo(() => readSearchParam(search), [search]);
  const setValue = React.useCallback(
    (next: string) => {
      void (navigate as unknown as AnyNavigate)({
        search: (prev) => buildNextSearchParam(prev, next),
        replace: true,
      });
    },
    [navigate],
  );
  return [value, setValue];
}
